import React, { useReducer, useEffect, Dispatch, Reducer } from 'react'

import '../lib/ui/Form.css'
import './ProfessionalDetails.css'
import { authedPost, authedPut, authedDelete } from '../lib/util/http'
import { formatDbDate } from '../lib/util/formatters'
import { Route, Action } from '../types/app'
import { AVProject, AVProfessional } from '../types/entities'
import { mapProfessional, mapProjects } from '../mappers/entities'
import { SymbolButton } from '../lib/ui/SymbolButton'
import { routeToPath } from '../routing'
import { loadProfessional } from '../actions/asyncActions'

export interface ProfessionalDetailsState {
  professional?: AVProfessional
  error?: string
  result?: string
  oldEmail?: string
  projects: AVProject[]
}

interface ProfessionalResponse {
  error?: string
  data?: object
}

const handleProfessionalResponse = (
  response: ProfessionalResponse,
  actionVerb: string,
  dispatch: Function
) => {
  if (response.error) {
    dispatch({ name: 'ERROR', payload: response.error })
  } else {
    dispatch({
      name: 'UPDATE_RESULT',
      payload: {
        result: `Successfully ${actionVerb} user.`,
        user: response.data,
      },
    })
  }
}

const createProfessional = async (
  professional: AVProfessional,
  dispatch: Function
) => {
  const result = await authedPost(`av/professionals`, professional)
  const json = await result.json()
  routeToPath(`/?page=professional_details&id=${json.data.ID}`)
}

const changeEmailWarning = `WARNING! This update has a security risk. Verify the new email belongs to the professional before changing it.
Changing an email will result in a new activation mail being sent to the new email address.`

const updateProfessional = async (
  professional: AVProfessional,
  oldEmail: string | undefined,
  dispatch: Function
) => {
  //console.log(professional)
  if (!professional.UserID && professional.Email) {
    if (
      !window.confirm(
        'Setting an email for the professional will create an account in COPRA and email them a link to activate it. Please confirm?'
      )
    ) {
      return
    }
  }
  const isEmailChange = oldEmail && professional.Email !== oldEmail
  if (isEmailChange) {
    if (!window.confirm(changeEmailWarning)) {
      return
    }
  }

  const result = await authedPut(`av/professionals/${professional.ID}`, {
    ...professional,
    IsEmailChange: isEmailChange,
  })
  const json = await result.json()
  handleProfessionalResponse(json, 'updated', dispatch)
}

const deleteProfessional = async (professional: AVProfessional) => {
  if (window.confirm('Are you sure you want to remove this professional?')) {
    const result = await authedDelete(`av/professionals/${professional.ID}`, {})
    await result.json()
    routeToPath(`/`)
  }
}

const resetPassword = async (
  professional: AVProfessional,
  dispatch: Function
) => {
  if (
    window.confirm(
      `This will send a password reset link to the professional's email address. Are you sure?`
    )
  ) {
    const result = await authedPut(
      `av/professionals/${professional.ID}/reset-password`,
      {}
    )
    const json = await result.json()
    if (json.error) {
      dispatch({ name: 'ERROR', payload: json.error })
    } else {
      dispatch({
        name: 'RESULT',
        payload: `Successfully reset professional's password.`,
      })
    }
  }
}

const initialDetailsState: ProfessionalDetailsState = {
  professional: undefined,
  error: '',
  result: '',
  projects: [],
}

const cutoffLabels = {
  LAST_CALENDAR_DAY_OF_MONTH: 'Last calendar day of month',
  LAST_FRIDAY_OF_MONTH: 'Last Friday of month',
}

const reducer: React.Reducer<ProfessionalDetailsState, Action> = (
  state: ProfessionalDetailsState,
  action: Action
) => {
  switch (action.name) {
    case 'PROFESSIONAL_EDIT':
      return {
        ...state,
        professional: {
          ...state.professional,
          [action.payload.field]: action.payload.value,
        } as AVProfessional,
      }
    case 'PROJECT_SELECT':
      return {
        ...state,
        selectedProject: action.payload,
      }
    case 'PROFESSIONAL_LOADED':
      const prof = mapProfessional(action.payload)
      return {
        ...state,
        professional: prof,
        projects: action.payload ? mapProjects(action.payload) : [],
        oldEmail: prof.Email,
      } as ProfessionalDetailsState
    case 'ERROR':
      return {
        ...state,
        error: action.payload,
      }
    case 'RESULT':
      return {
        ...state,
        result: action.payload,
      }
    case 'UPDATE_RESULT':
      return {
        ...state,
        error: '',
        result: action.payload.result,
        professional: {
          ...state.professional,
          ...action.payload.user,
        },
        oldEmail: action.payload.user.Email,
      }
    default:
      console.error(`Invalid action: `, action)
      throw new Error(`Invalid action: ${action}`)
  }
}

interface Props {
  route: Route
  navigate: Function
}

const editProject = (dispatch: Dispatch<Action>, project: AVProject) => {
  routeToPath(`/?page=project_details&id=${project.ID}`)
}

const newProject = (dispatch: Dispatch<Action>, professionalId: string) => {
  routeToPath(`/?page=project_details&professional_id=${professionalId}`)
}

const loadAvProfessional = async (id: string, dispatch: Function) => {
  if (id) {
    const professional = await loadProfessional(id)
    dispatch({ name: 'PROFESSIONAL_LOADED', payload: professional })
  } else {
    dispatch({ name: 'PROFESSIONAL_LOADED', payload: undefined })
  }
}

export const ProfessionalDetails = ({ route }: Props) => {
  const [state, dispatch] = useReducer<
    Reducer<ProfessionalDetailsState, Action>
  >(reducer, initialDetailsState)

  useEffect(() => {
    loadAvProfessional(route.params.id, dispatch)
  }, [route.params.id])

  const professional = state.professional
  const projects = state.projects

  if (!professional) {
    return <div>Loading...</div>
  }

  return (
    <div>
      <div className="top-nav">
        <button
          className="btn-back"
          title="Back"
          onClick={(e) => routeToPath('/')}
        ></button>
        <div className="top-nav-spacer"></div>
        {!professional.ID && (
          <button
            className="btn-save"
            title="Create professional"
            onClick={(e) => createProfessional(professional, dispatch)}
          ></button>
        )}
        {!!professional.ID && (
          <button
            className="btn-save"
            title="Save professional"
            onClick={(e) =>
              updateProfessional(professional, state.oldEmail, dispatch)
            }
          ></button>
        )}
        {!!professional.ID && (
          <button
            className="btn-delete"
            title="Remove professional"
            onClick={(e) => deleteProfessional(professional)}
          ></button>
        )}
      </div>
      <div className="white bg-red head">Professional</div>
      {state.error && <div className="error">{state.error}</div>}
      {state.result && <div>{state.result}</div>}
      <div className="contract-form">
        <div className="contract-form-title">Professional details</div>
        <div className="form-inline">
          <div>
            <div className="label">Bullhorn ID</div>
            <div className="value">
              <input
                onChange={(e) =>
                  dispatch({
                    name: 'PROFESSIONAL_EDIT',
                    payload: { field: 'CodeAV', value: e.target.value },
                  })
                }
                value={professional.CodeAV}
              />
            </div>
          </div>
          <div>
            <div className="label">First name</div>
            <div className="value">
              <input
                onChange={(e) =>
                  dispatch({
                    name: 'PROFESSIONAL_EDIT',
                    payload: { field: 'FirstName', value: e.target.value },
                  })
                }
                value={professional.FirstName}
              />
            </div>
          </div>
          <div>
            <div className="label">Last name</div>
            <div className="value">
              <input
                onChange={(e) =>
                  dispatch({
                    name: 'PROFESSIONAL_EDIT',
                    payload: { field: 'LastName', value: e.target.value },
                  })
                }
                value={professional.LastName}
              />
            </div>
          </div>
          <div>
            <div className="label">Email address</div>
            <div className="value">
              <input
                className="input-email"
                onChange={(e) =>
                  dispatch({
                    name: 'PROFESSIONAL_EDIT',
                    payload: { field: 'Email', value: e.target.value },
                  })
                }
                value={professional.Email}
              />
            </div>
          </div>
          {!!(professional.Email && professional.UserID) && (
            <div>
              <div className="label">
                <button
                  className="btn"
                  onClick={(e) => resetPassword(professional, dispatch)}
                >
                  Reset password
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
      {route.params.id && (
        <div className="contract-form">
          <div className="contract-form-title">Project details</div>
          <table cellSpacing="0" style={{ width: '100%' }}>
            <thead>
              <tr className="bg-l-grey">
                <th>Code</th>
                <th>Type</th>
                <th>Start date</th>
                <th>End date</th>
                <th>Magno Entity</th>
                <th>Cutoff type</th>
                <th>Unit type</th>
                <th>Reimbursed</th>
                <th>Deductible</th>
                <th>Approver</th>
                <th>
                  <SymbolButton
                    className="float-right"
                    icon="asterisk"
                    onClick={() => newProject(dispatch, route.params.id)}
                  />
                </th>
              </tr>
            </thead>
            <tbody>
              {projects.map((project) => (
                <tr key={project.ID}>
                  <td>{project.ProjectCode}</td>
                  <td>{project.ProjectType}</td>
                  <td>
                    {project.StartDate && formatDbDate(project.StartDate)}
                  </td>
                  <td>{project.EndDate && formatDbDate(project.EndDate)}</td>
                  <td>{project.MagnoEntity}</td>
                  <td>
                    {project.CutoffPoint && cutoffLabels[project.CutoffPoint]}
                  </td>
                  <td>{project.UnitType}</td>
                  <td>
                    {project.ExpensesReimbursed ? 'Exp ' : ''}
                    {project.TravelReimbursed ? 'KM' : ''}
                  </td>
                  <td>
                    {project.TravelDeductible ? 'KM ' : ''}
                    {project.TravelExpensesDeductible ? 'TrvExp ' : ''}
                    {project.ExpensesDeductible ? 'OthExp' : ''}
                  </td>
                  <td>
                    {project.Approvers.map((approver) => approver.Name).join(
                      ', '
                    )}
                  </td>
                  <td>
                    <SymbolButton
                      icon="pen"
                      onClick={() => editProject(dispatch, project)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
